import { DrawableObject, RenderConfiguration, RenderMode, RenderSettings } from '../renderer';
import { PropertyAnimation } from '../animation';
import { ObjectInteraction } from '../object';
import { Context } from '../context/Context';
import { Camera } from '../camera';
import { PathNode } from '../navigation-mesh';

const Y_OFFSET = 0.15;
const WALL_HEIGHT = 3.0;
const SURFACE_ALPHA = 0.35;
const WALL_BOTTOM_ALPHA = 0.3;
const FLOATS_PER_VERTEX = 7; // x, y, z, r, g, b, a

export class VisualPathNodes implements DrawableObject {
  public renderConfiguration: RenderConfiguration = {
    order: 6000,
    culling: 'none',
    static: true,
    modes: [RenderMode.Overlay],
  };
  animatedProperties?: PropertyAnimation;
  interaction?: ObjectInteraction;

  private vao: WebGLVertexArrayObject;
  private glBuffer: WebGLBuffer;
  private vertexCount = 0;

  constructor(private ctx: Context) {
    const gl = ctx.gl;
    this.glBuffer = gl.createBuffer()!;
    this.vao = gl.createVertexArray()!;

    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.glBuffer);

    const stride = FLOATS_PER_VERTEX * Float32Array.BYTES_PER_ELEMENT;
    // location 0: aVertexPosition (vec3)
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, stride, 0);
    // location 3: aVertexColor (vec4)
    gl.enableVertexAttribArray(3);
    gl.vertexAttribPointer(3, 4, gl.FLOAT, false, stride, 3 * Float32Array.BYTES_PER_ELEMENT);

    gl.bindVertexArray(null);
  }

  public render(ctx: Context, camera: Camera, settings: RenderSettings) {
    if (this.vertexCount === 0) return;

    const gl = ctx.gl;
    const shader = ctx.engine.shaderManager.getTestGeometryShader();
    ctx.setShader(shader);

    gl.bindVertexArray(this.vao);

    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.GEQUAL);
    gl.depthMask(false);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.disable(gl.CULL_FACE);

    shader.uniformsContainer
      .setModelViewMatrix(camera.modelViewMatrix)
      .setProjectionMatrix(camera.projectionMatrix);

    gl.drawArrays(gl.TRIANGLES, 0, this.vertexCount);

    gl.depthMask(true);
    gl.disable(gl.BLEND);
    gl.enable(gl.CULL_FACE);
    gl.bindVertexArray(null);
  }

  public setPathNodes(nodes: PathNode[]) {
    const geo: number[] = [];

    for (let i = 0; i < nodes.length; i++) {
      const [r, g, b] = pastelColor(i);
      const node = nodes[i];

      this.emitSurface(geo, node, r, g, b);
      this.emitBorderWalls(geo, node, r, g, b);
    }

    const gl = this.ctx.gl;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.glBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(geo), gl.STATIC_DRAW);
    this.vertexCount = geo.length / FLOATS_PER_VERTEX;
  }

  private emitSurface(geo: number[], node: PathNode, r: number, g: number, b: number) {
    const min = node.min;
    const max = node.max;
    const y = (min[1] as number) + Y_OFFSET;

    // Flat quad at node's min Y (two triangles)
    vertex(geo, [min[0], y, min[2]], 0, r, g, b, SURFACE_ALPHA);
    vertex(geo, [max[0], y, min[2]], 0, r, g, b, SURFACE_ALPHA);
    vertex(geo, [min[0], y, max[2]], 0, r, g, b, SURFACE_ALPHA);

    vertex(geo, [max[0], y, min[2]], 0, r, g, b, SURFACE_ALPHA);
    vertex(geo, [max[0], y, max[2]], 0, r, g, b, SURFACE_ALPHA);
    vertex(geo, [min[0], y, max[2]], 0, r, g, b, SURFACE_ALPHA);
  }

  private emitBorderWalls(geo: number[], node: PathNode, r: number, g: number, b: number) {
    const min = node.min;
    const max = node.max;
    const eps = 0.1;

    const hasNeighbour = [false, false, false, false]; // east, west, north, south
    for (const n of node.neighbours) {
      if (n.min[0] >= (max[0] as number) - eps && n.min[0] <= (max[0] as number) + eps)
        hasNeighbour[0] = true;
      if (n.max[0] >= (min[0] as number) - eps && n.max[0] <= (min[0] as number) + eps)
        hasNeighbour[1] = true;
      if (n.min[2] >= (max[2] as number) - eps && n.min[2] <= (max[2] as number) + eps)
        hasNeighbour[2] = true;
      if (n.max[2] >= (min[2] as number) - eps && n.max[2] <= (min[2] as number) + eps)
        hasNeighbour[3] = true;
    }

    const steps = 8;

    // East (max X) / West (min X) — walls along Z axis
    for (let side = 0; side < 2; side++) {
      if (hasNeighbour[side]) continue;
      const x = (side === 0 ? max[0] : min[0]) as number;
      const zMin = min[2] as number;
      const zSpan = (max[2] as number) - zMin;
      for (let i = 0; i < steps; i++) {
        const z0 = zMin + (zSpan * i) / steps;
        const z1 = zMin + (zSpan * (i + 1)) / steps;
        this.emitWallQuad(geo, x, z0, x, z1, node, r, g, b);
      }
    }

    // North (max Z) / South (min Z) — walls along X axis
    for (let side = 2; side < 4; side++) {
      if (hasNeighbour[side]) continue;
      const z = (side === 2 ? max[2] : min[2]) as number;
      const xMin = min[0] as number;
      const xSpan = (max[0] as number) - xMin;
      for (let i = 0; i < steps; i++) {
        const x0 = xMin + (xSpan * i) / steps;
        const x1 = xMin + (xSpan * (i + 1)) / steps;
        this.emitWallQuad(geo, x0, z, x1, z, node, r, g, b);
      }
    }
  }

  private emitWallQuad(
    geo: number[],
    x0: number,
    z0: number,
    x1: number,
    z1: number,
    node: PathNode,
    r: number,
    g: number,
    b: number,
  ) {
    const h0 = (node.min[1] as number) + Y_OFFSET;
    const h1 = h0;
    const t0 = h0 + WALL_HEIGHT;
    const t1 = h1 + WALL_HEIGHT;

    // Two triangles forming a quad, bottom opaque-ish, top transparent
    vertex(geo, [x0, h0, z0], 0, r, g, b, WALL_BOTTOM_ALPHA);
    vertex(geo, [x1, h1, z1], 0, r, g, b, WALL_BOTTOM_ALPHA);
    vertex(geo, [x0, t0, z0], 0, r, g, b, 0);

    vertex(geo, [x1, h1, z1], 0, r, g, b, WALL_BOTTOM_ALPHA);
    vertex(geo, [x1, t1, z1], 0, r, g, b, 0);
    vertex(geo, [x0, t0, z0], 0, r, g, b, 0);
  }
}

function vertex(
  geo: number[],
  pos: { [index: number]: number },
  yOffset: number,
  r: number,
  g: number,
  b: number,
  a: number,
) {
  geo.push(pos[0], pos[1] + yOffset, pos[2], r, g, b, a);
}

function pastelColor(index: number): [number, number, number] {
  const hue = (index * 0.618033988749895) % 1.0;
  return hslToRgb(hue, 0.45, 0.72);
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h * 6) % 2) - 1));
  const m = l - c / 2;

  let r: number, g: number, b: number;
  if (h < 1 / 6) {
    r = c;
    g = x;
    b = 0;
  } else if (h < 2 / 6) {
    r = x;
    g = c;
    b = 0;
  } else if (h < 3 / 6) {
    r = 0;
    g = c;
    b = x;
  } else if (h < 4 / 6) {
    r = 0;
    g = x;
    b = c;
  } else if (h < 5 / 6) {
    r = x;
    g = 0;
    b = c;
  } else {
    r = c;
    g = 0;
    b = x;
  }

  return [r + m, g + m, b + m];
}
